import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { BookOpen, Trophy, User, Ticket, CheckCircle2, AlertCircle } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  if (!user || user === false) return null;

  const active = user.subscription_active;
  const expiry = user.subscription_expiry ? new Date(user.subscription_expiry).toLocaleDateString() : null;

  const tiles = [
    { to: "/pays", icon: BookOpen, t: t("dashboard.t1"), d: t("dashboard.d1") },
    { to: "/quiz", icon: Trophy, t: t("dashboard.t2"), d: t("dashboard.d2") },
    { to: "/profil", icon: User, t: t("dashboard.t3"), d: t("dashboard.d3") },
    { to: "/tarifs", icon: Ticket, t: t("dashboard.t4"), d: t("dashboard.d4") },
  ];

  return (
    <div className="max-w-6xl mx-auto px-5 py-14 paper-noise min-h-[70vh]">
      <span className="text-xs tracking-[0.25em] uppercase font-semibold text-[#E06D53]">{t("dashboard.badge")}</span>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tighter text-[#2C2A28] mt-3 mb-10">{t("dashboard.hello")} {user.profile?.first_name || user.email.split("@")[0]} 👋</h1>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className={`rounded-2xl p-6 mb-10 flex items-center justify-between flex-wrap gap-4 ${active ? "bg-[#EAF4EF] border border-[#BEE3D0]" : "bg-[#FBEDE9] border border-[#F2CFC5]"}`}
        data-testid="subscription-banner">
        <div className="flex items-center gap-3">
          {active ? <CheckCircle2 className="w-8 h-8 text-[#2D6A4F]" /> : <AlertCircle className="w-8 h-8 text-[#E06D53]" />}
          <div>
            <h3 className="font-medium text-[#2C2A28] text-lg">{active ? t("dashboard.subActive") : t("dashboard.subInactive")}</h3>
            <p className="text-sm text-[#5C5854]">{active ? (user.role === "admin" ? t("dashboard.subAdmin") : t("dashboard.validUntil", { date: expiry })) : t("dashboard.unlock")}</p>
          </div>
        </div>
        {!active && <Link to="/tarifs"><Button className="rounded-full bg-[#E06D53] hover:bg-[#C95B42] text-white" data-testid="dashboard-subscribe">{t("dashboard.subscribe")}</Button></Link>}
      </motion.div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {tiles.map((c, i) => (
          <motion.div key={c.to} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
            <Link to={c.to} className="block bg-white rounded-2xl border border-[#E5E0D8] p-6 tc-card-hover h-full" data-testid={`dashboard-tile-${c.to.slice(1)}`}>
              <span className="w-11 h-11 rounded-full bg-[#FBEDE9] flex items-center justify-center mb-4"><c.icon className="w-5 h-5 text-[#E06D53]" /></span>
              <h4 className="font-medium text-[#2C2A28]">{c.t}</h4>
              <p className="text-sm text-[#5C5854] mt-1">{c.d}</p>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
